import { fetcher } from '@common/utils/fetcher';

import { type User } from './UsersService';

interface Filters {
  looking_for?: 0 | 1 | 2;
  age_from?: number;
  age_to?: number;
  has_about?: boolean;
  is_verified?: boolean;
  goal?: string[];
  alcohol?: string[];
  smoking?: string[];
  children?: string[];
  zodiac?: string[];
  height_from?: number;
  height_to?: number;
}

// The filters are the request's identity, and so its SWR key. `limit` is not
// one of them: it changes with every swipe, and goes to getFeed on its own.
export type GetFeedParams = Filters;

export type FeedAdvertising = {
  ad: boolean;
};

export type FeedResponse = User | FeedAdvertising;

export type GetFeedResponse = {
  results: FeedResponse[];
  page: number;
  hasMore: boolean;
  total: number;
};

// A feed request that has not answered by then is given up, so that the list
// can retry it instead of waiting on it for good.
export const FEED_REQUEST_TIMEOUT_MS = 15 * 1000;

// The most entries one page holds, ad placeholders included. The server
// clamps `limit` to it (FEED-CORE 5.6).
export const FEED_PAGE_MAX_ENTRIES = 70;

/** getFeed's rejection when it made no request, because the page had no room. */
export class FeedRequestSkipped extends Error {}

/**
 * GET /feed. `limit` is how many entries the page may hold, ad placeholders
 * included: what the client will keep. Below 1 there is nothing to ask for
 * (the server answers 400), so no request is made and the promise rejects
 * with FeedRequestSkipped.
 */
export const getFeed = ({
  queryParams,
  limit,
}: {
  queryParams?: GetFeedParams;
  limit?: number;
}) => {
  if (limit !== undefined && !(limit >= 1)) {
    return Promise.reject(
      new FeedRequestSkipped(
        `No feed request: the page has no room (limit ${limit})`,
      ),
    );
  }

  return fetcher<GetFeedResponse>({
    url: '/feed',
    method: 'GET',
    params: limit === undefined ? queryParams : { ...queryParams, limit },
    timeout: FEED_REQUEST_TIMEOUT_MS,
  });
};
